"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { ChevronLeft, ChevronRight, ArrowRight, Shield, CheckCircle, Truck } from "lucide-react"
import Image from "next/image"
import Link from "next/link"
import { useState } from "react"
import { useTranslations } from "next-intl";
import { useRouter } from "@/i18n/navigation"
import { OrganizationJsonLd, WebsiteJsonLd, ProductCatalogJsonLd } from "@/components/JsonLd"
import Head from "next/head"

export default function LandingPage() {
  const t = useTranslations("Landing");

  const [currentSlide, setCurrentSlide] = useState(0)
  // const [currentPartnerSlide, setCurrentPartnerSlide] = useState(0)
  const router = useRouter();
  const adSlides = [
    { id: 1, image: "/landing/ad/EN_Bandeau_Site_X_Claw-05-05.jpg?height=400&width=1420&text=Ad+Slide+1" },
    { id: 2, image: "/landing/ad/EN_Bandeau_Worldskills_Plan_de_travail_1-02_Plan_de_travail_1-02.jpg?height=400&width=1420&text=Ad+Slide+2" },
    { id: 3, image: "/landing/ad/EN_SITE.jpg?height=400&width=1ż20&text=Ad+Slide+3" },
  ]

  const products = [
    { id: 1, name: "LITE-COM", image: "/featuredProduct1.webp?height=256&width=256&text=Helmet" },
    { id: 2, name: "Kit Retenue MO71610", image: "/featuredProduct2.webp?height=256&width=256&text=Gloves" },
    { id: 3, name: "PHYTOSANITARY", image: "/featuredProduct3.webp?height=256&width=256&text=Boots" },
    { id: 4, name: "HOTARU", image: "/featuredProduct4.webp?height=256&width=256&text=Vest" },
    { id: 5, name: "EUROCUT N505", image: "/featuredProduct5.webp?height=256&width=256&text=Glasses" },
  ]

  // const partners = [
  //   { id: 1, name: "3M", image: "/placeholder.svg?height=150&width=150&text=3M" },
  //   { id: 2, name: "Honeywell", image: "/placeholder.svg?height=150&width=150&text=Honeywell" },
  //   { id: 3, name: "MSA Safety", image: "/placeholder.svg?height=150&width=150&text=MSA+Safety" },
  //   { id: 4, name: "Ansell", image: "/placeholder.svg?height=150&width=150&text=Ansell" },
  //   { id: 5, name: "DuPont", image: "/placeholder.svg?height=150&width=150&text=DuPont" },
  //   { id: 6, name: "Uvex", image: "/placeholder.svg?height=150&width=150&text=Uvex" },
  //   { id: 7, name: "Moldex", image: "/placeholder.svg?height=150&width=150&text=Moldex" },
  //   { id: 8, name: "Portwest", image: "/placeholder.svg?height=150&width=150&text=Portwest" },
  // ]

  return (
    <>
      {/* SEO Structured Data */}
      <OrganizationJsonLd />
      <WebsiteJsonLd />
      <ProductCatalogJsonLd />
      
      <div className="min-h-screen">
        {/* Section 1 - Hero */}
        <section className="relative h-screen flex items-center justify-center bg-linear-to-br from-primary to-primary/80">
          <div
            className="absolute inset-0 bg-cover bg-center opacity-60"
            style={{
              backgroundImage: "url('/coverguard/forest.png?height=1080&width=1920&text=Hero+Background')", // 
            }}
          />
          <div className="relative z-10 text-center text-white max-w-4xl mx-auto px-4">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold mb-4 md:mb-6">{ t("title") }</h1>
            <p className="text-lg sm:text-xl md:text-2xl mb-6 md:mb-8 opacity-90">
              { t("about") }
            </p>
            <Button size="lg" variant="secondary" className="text-base md:text-lg px-6 md:px-8 py-3 md:py-4" onClick={() => { router.push("/contact") }}>
              {t("cta")}
              <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5" />
            </Button>
          </div>
        </section>

        {/* Section 1.5 - SEO Benefits Section */}
        <section className="py-12 md:py-16 bg-gray-50 hidden">
          <div className="container mx-auto px-4">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">{t("headline")}</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                {t("subheading")}
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              <Card className="text-center p-6">
                <CardContent className="pt-6">
                  <Shield className="w-12 h-12 mx-auto mb-4 text-primary" />
                  <h3 className="font-semibold mb-2">{t("bullets.certified")}</h3>
                  <p className="text-sm text-gray-600">
                    Все наши СИЗ соответствуют международным стандартам безопасности и имеют необходимые сертификаты качества.
                  </p>
                </CardContent>
              </Card>
              
              <Card className="text-center p-6">
                <CardContent className="pt-6">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-primary" />
                  <h3 className="font-semibold mb-2">{t("bullets.consultation")}</h3>
                  <p className="text-sm text-gray-600">
                    Наши эксперты помогут подобрать оптимальные средства защиты для вашей отрасли и специфики работы.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Section 2 - Sliders */}
      <section className="py-12 md:py-16 bg-white">
        <div className="container mx-auto px-4">
          {/* Ad Slider */}
          <div className="mb-12 md:mb-16">
            <div className="relative max-w-8xl mx-auto">
              <div className="overflow-hidden rounded-lg">
                <Image
                  src={adSlides[currentSlide].image || "/placeholder.svg"}
                  alt={`Advertisement ${currentSlide + 1}`}
                  width={1420}
                  height={400}
                  className="w-full h-[200px] sm:h-[300px] md:h-[400px] object-cover"
                />
              </div>
              <Button
                variant="outline"
                size="icon"
                className="absolute left-2 md:left-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white w-8 h-8 md:w-10 md:h-10"
                onClick={() => setCurrentSlide((prev) => (prev - 1 + adSlides.length) % adSlides.length)}
              >
                <ChevronLeft className="w-3 h-3 md:w-4 md:h-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="absolute right-2 md:right-4 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white w-8 h-8 md:w-10 md:h-10"
                onClick={() => setCurrentSlide((prev) => (prev + 1) % adSlides.length)}
              >
                <ChevronRight className="w-3 h-3 md:w-4 md:h-4" />
              </Button>
            </div>
          </div>

          {/* Product Slider */}
          <div>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 md:mb-8 gap-4">
              <h2 className="text-2xl md:text-3xl font-bold">{t("featuredProducts")}</h2>
              <Link href="/catalogue?page=1&families=Eye+Protection">
                <Button variant="outline" size="sm" className="w-full sm:w-auto bg-transparent">
                  {t("viewAllProducts")}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-6">
                {products.map((product) => (
                  <Card key={product.id} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-3 md:p-4" onClick={() => window.location.href = `/catalogue/?page=1&families=Eye+Protection`}>
                      <Image
                        src={product.image || "/placeholder.svg"}
                        alt={product.name}
                        width={256}
                        height={256}
                        className="w-full h-48 md:h-64 object-cover rounded-lg mb-3 md:mb-4"
                      />
                      <div className="h-[80px] md:h-[100px] flex items-center justify-center">
                        <h3 className="text-center font-medium text-sm md:text-base">{product.name}</h3>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 - Ad with Text */}
      <section className="py-12 md:py-16 bg-secondary">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
            <div className="order-2 lg:order-1 flex justify-center lg:justify-center">
              <Image
                src="/safetydesigner.png?height=500&width=700&text=Safety+Equipment+Ad"
                alt={t("imageAlt")}
                width={400}
                height={300}
                className="rounded-lg shadow-lg"
              />
            </div>
            <div className="space-y-4 md:space-y-6 order-1 lg:order-2 text-center lg:text-left">
              <h2 className="text-3xl md:text-4xl font-bold text-primary">
                {t("headline")}
              </h2>
              <p className="text-base md:text-lg text-gray-700">
                {t("subheading")}
              </p>
              <ul className="space-y-2 md:space-y-3 text-gray-700">
                {["certified", "consultation"].map((key) => (
                  <li
                    key={key}
                    className="flex items-center justify-center lg:justify-start"
                  >
                    <div className="w-2 h-2 bg-primary rounded-full mr-3" />
                    {t(`bullets.${key}`)}
                  </li>
                ))}
              </ul>
              <Button size="lg" className="mt-4 md:mt-6 w-full sm:w-auto" onClick={() => { router.push("/contact") }}>
                {t("cta")}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4 - Our Partners */}
      {/* <section className="py-12 md:py-16 bg-white" style={{ display: "none" }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 md:mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Our Partners</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We collaborate with the world's leading safety equipment manufacturers to bring you the highest quality
              products and innovative solutions. Our partnerships ensure access to cutting-edge technology and
              industry-leading standards.
            </p>
          </div>

          <div className="relative">
            <div className="overflow-hidden">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6 md:gap-8">
                {partners.map((partner) => (
                  <div key={partner.id} className="text-center group">
                    <div className="w-24 h-24 md:w-32 md:h-32 lg:w-[150px] lg:h-[150px] mx-auto mb-3 md:mb-4 bg-gray-50 rounded-lg flex items-center justify-center group-hover:bg-gray-100 transition-colors">
                      <Image
                        src={partner.image || "/placeholder.svg"}
                        alt={partner.name}
                        width={150}
                        height={150}
                        className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 object-contain"
                      />
                    </div>
                    <p className="text-sm md:text-base font-medium text-gray-700">{partner.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section> */}
      </div>
    </>
  )
}
